import './TruncatedValue.css';

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';
import { keepOverlayClear, scheduleOverlayClear } from './overlayPosition';

export type CopyState = 'copied' | 'failed' | null;

/** Copies text with the async clipboard API, falling back to a hidden textarea + execCommand. */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.top = '-1000px';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
};

const isCoarsePointer = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

const stop = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

export interface TruncatedValueProps {
  value: string | number | null | undefined;
  /** Column label used in the copy button name: "Copy <label>: <value>". */
  label: string;
  /** Touch panel extra action for the name cell, wired to the existing navigation. */
  onOpenDetails?: () => void;
  className?: string;
  /**
   * Shortened form with a middle ellipsis (see `middleEllipsis`), shown only while the full value does not fit its cell.
   * Everything else keeps working on the full value: hover / focus tooltip, copy control, touch panel, screen readers
   * (the shown text is named by the full value). A cell wide enough for the full value shows it.
   */
  shortDisplay?: string;
}

const TruncatedValue: React.FC<TruncatedValueProps> = ({ value, label, onOpenDetails, className, shortDisplay }) => {
  const { t } = useTranslation();
  const full = value === null || value === undefined ? '' : String(value);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const ghostRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<OverlayPanel | null>(null);
  const timerRef = useRef<number | undefined>(undefined);
  const [clipped, setClipped] = useState(false);
  const [useShort, setUseShort] = useState(false);
  const hasShort = shortDisplay !== undefined && shortDisplay !== full;
  const truncated = clipped || (hasShort && useShort);
  const [copyState, setCopyState] = useState<CopyState>(null);
  const [coarse, setCoarse] = useState(isCoarsePointer);

  const measure = useCallback(() => {
    const node = textRef.current;
    if (node) {
      // the full value is measured in a hidden copy, so the cell can switch between the full and the shortened text
      const ghost = ghostRef.current;
      setUseShort(Boolean(ghost) && ghost!.offsetWidth > node.clientWidth + 1);
      setClipped(node.scrollWidth > node.clientWidth + 1);
    }
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [full, shortDisplay, measure]);

  useEffect(() => {
    const node = textRef.current;
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(() => measure());
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia('(pointer: coarse)');
    const listener = () => setCoarse(query.matches);
    if (query.addEventListener) {
      query.addEventListener('change', listener);
      return () => query.removeEventListener('change', listener);
    }
    return undefined;
  }, []);

  useEffect(() => () => window.clearTimeout(timerRef.current), []);

  const copy = async (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const ok = await copyTextToClipboard(full);
    setCopyState(ok ? 'copied' : 'failed');
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopyState(null), 1500);
  };

  const copyWord = t('rtuCluster.copy', { defaultValue: 'Copy' });
  const feedbackText =
    copyState === 'copied'
      ? t('rtuCluster.copied', { defaultValue: 'Copied' })
      : copyState === 'failed'
        ? t('rtuCluster.copyFailed', { defaultValue: 'Copy failed' })
        : '';

  const togglePanel = (event: React.SyntheticEvent) => {
    const target = event.currentTarget as Element;
    panelRef.current?.toggle(event);
    scheduleOverlayClear(() => panelRef.current?.getElement(), target);
  };

  const openPanelOnTap = (event: React.MouseEvent) => {
    if (coarse && truncated) {
      event.stopPropagation();
      event.preventDefault();
      togglePanel(event);
    }
  };

  // Keyboard users reach the value with Tab; Enter/Space on it behaves like a tap on touch.
  const onValueKeyDown = (event: React.KeyboardEvent) => {
    if (truncated && coarse && (event.key === 'Enter' || event.key === ' ')) {
      event.stopPropagation();
      event.preventDefault();
      togglePanel(event);
    }
  };

  const copyButton = (extraClass = '') => (
    <button
      type="button"
      className={`dms-truncated-value__copy ${extraClass}`.trim()}
      aria-label={`${copyWord} ${label}: ${full}`}
      onClick={copy}
      onMouseDown={stop}
      onDoubleClick={stop}
      onTouchEnd={stop}
      onKeyDown={stop}
      onKeyUp={stop}
    >
      <i className="pi pi-copy" aria-hidden="true" />
    </button>
  );

  return (
    <span className={`dms-truncated-value${truncated ? ' dms-truncated-value--truncated' : ''}${className ? ` ${className}` : ''}`}>
      <span
        ref={textRef}
        className="dms-truncated-value__text"
        tabIndex={truncated ? 0 : undefined}
        {...(hasShort && useShort ? { role: 'group', 'aria-label': full } : {})}
        onClick={openPanelOnTap}
        onKeyDown={onValueKeyDown}
      >
        {hasShort && useShort ? shortDisplay : full}
      </span>
      {hasShort && (
        <span ref={ghostRef} className="dms-truncated-value__ghost" aria-hidden="true" data-text={full} />
      )}
      {truncated && (
        <Tooltip
          target={textRef}
          content={full}
          event="both"
          position="top"
          disabled={coarse}
          className="dms-truncated-value__tooltip"
        />
      )}
      {truncated && copyButton()}
      <span className="dms-truncated-value__feedback" role="status" aria-live="polite" data-state={copyState ?? 'idle'}>
        {feedbackText}
      </span>
      {truncated && coarse && (
        <OverlayPanel
          ref={panelRef}
          dismissable
          closeOnEscape
          className="dms-truncated-value__panel"
          onShow={() => keepOverlayClear(panelRef.current?.getElement(), textRef.current)}
        >
          <div
            className="dms-truncated-value__panel-body"
            onClick={stop}
            onMouseDown={stop}
            onTouchEnd={stop}
          >
            <div className="dms-truncated-value__panel-label">{label}</div>
            <div className="dms-truncated-value__panel-value">{full}</div>
            <div className="dms-truncated-value__panel-actions">
              <button type="button" className="dms-truncated-value__panel-button" onClick={copy}>
                <i className="pi pi-copy" aria-hidden="true" /> {copyWord}
              </button>
              {onOpenDetails && (
                <button
                  type="button"
                  className="dms-truncated-value__panel-button"
                  onClick={(event) => {
                    event.stopPropagation();
                    panelRef.current?.hide();
                    onOpenDetails();
                  }}
                >
                  <i className="pi pi-external-link" aria-hidden="true" />{' '}
                  {t('rtuCluster.openDetails', { defaultValue: 'Open RTU details' })}
                </button>
              )}
              <span className="dms-truncated-value__panel-feedback" role="status" aria-live="polite">
                {feedbackText}
              </span>
            </div>
          </div>
        </OverlayPanel>
      )}
    </span>
  );
};

export default TruncatedValue;
